import { useState, useRef, useCallback, useEffect } from 'react';
import maplibregl from 'maplibre-gl';
import { uiCategoryOf, iconStyle, iconColor, categoryLabel } from '../utils/events/categories';
import { glyphSvg } from '../utils/events/icons';
import { statusOf, fmtRange, STATUS_META } from '../utils/events/time';

const EVENTS_API = 'https://events-maps-pub-int.delhivery.com/events';

// Line/source ids for polyline (MultiLineString) events.
const LINE_SOURCE_ID = 'events-lines';
const LINE_LAYER_ID = 'events-lines-layer';
const LINE_CASING_LAYER_ID = 'events-lines-casing';

// Source/layer ids for polygon (MultiPolygon) events.
const POLY_SOURCE_ID = 'events-polygons';
const POLY_FILL_LAYER_ID = 'events-polygons-fill';
const POLY_OUTLINE_LAYER_ID = 'events-polygons-outline';

const FETCH_DEBOUNCE_MS = 350;

/** Severity → coloured pill for the popup. */
const SEVERITY_META = {
  High: { color: '#B42318', bg: '#FEF3F2' },
  Medium: { color: '#B54708', bg: '#FFFAEB' },
  Low: { color: '#175CD3', bg: '#EFF8FF' },
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Custom hook for showing live logistics events (protests, floods, closures…)
 * on the map. Fetches the events feed for the current viewport, renders point
 * events as DOM markers with a category glyph and polyline events as a line
 * layer. Clicking an event opens a detail popup.
 */
export const useEvents = (mapRef) => {
  const [isEventsEnabled, setIsEventsEnabled] = useState(false);
  const [eventCount, setEventCount] = useState(0);

  const markersRef = useRef([]);
  const popupRef = useRef(null);
  const fetchTimeoutRef = useRef(null);
  const seenIdsRef = useRef(new Set()); // Dedup point markers across pans
  const lineFeaturesRef = useRef([]);   // Accumulated polyline features
  const polygonFeaturesRef = useRef([]); // Accumulated polygon features
  const abortRef = useRef(null);
  const enabledRef = useRef(false);

  // Build the rich popup HTML for a single event's properties.
  const buildPopupHtml = useCallback((props, uiCategory) => {
    const status = statusOf(props);
    const st = STATUS_META[status] || STATUS_META.unknown;
    const sev = SEVERITY_META[props.severity] || null;
    const { bg, fg } = iconStyle(uiCategory);
    const disc = `
      <span style="display:inline-flex;align-items:center;justify-content:center;
        width:28px;height:28px;border-radius:50%;background:${bg};color:${fg};flex-shrink:0;">
        ${glyphSvg(uiCategory, 18, fg)}
      </span>`;

    const title = escapeHtml(props.event_subtype || props.event_type || categoryLabel(uiCategory));
    const place = escapeHtml(
      [props.city, props.district, props.state].filter(Boolean).join(', ')
    );

    const chips = [
      `<span style="font-size:11px;font-weight:600;padding:2px 8px;border-radius:12px;color:${st.color};background:${st.bg};">${st.label}</span>`,
    ];
    if (sev) {
      chips.push(
        `<span style="font-size:11px;font-weight:600;padding:2px 8px;border-radius:12px;color:${sev.color};background:${sev.bg};">${escapeHtml(props.severity)} severity</span>`
      );
    }
    chips.push(
      `<span style="font-size:11px;font-weight:600;padding:2px 8px;border-radius:12px;color:#475467;background:#F2F4F7;">${escapeHtml(categoryLabel(uiCategory))}</span>`
    );

    const row = (label, value) =>
      value
        ? `<div style="margin-top:8px;"><div style="font-size:10px;font-weight:700;letter-spacing:.03em;text-transform:uppercase;color:#98A2B3;">${label}</div><div style="font-size:12.5px;line-height:1.45;color:#344054;margin-top:2px;">${escapeHtml(value)}</div></div>`
        : '';

    const when = fmtRange(props.event_start, props.event_end);
    const pincodes = Array.isArray(props.pincodes_affected)
      ? props.pincodes_affected.join(', ')
      : props.pincodes_affected;

    let sources = '';
    if (Array.isArray(props.source_examples) && props.source_examples.length) {
      const links = props.source_examples
        .slice(0, 3)
        .map(
          (u, i) =>
            `<a href="${escapeHtml(u)}" target="_blank" rel="noopener noreferrer" style="color:#1570EF;text-decoration:none;font-size:11.5px;">Source ${i + 1}</a>`
        )
        .join(' · ');
      sources = `<div style="margin-top:10px;display:flex;gap:8px;flex-wrap:wrap;">${links}</div>`;
    }

    return `
      <div style="max-width:320px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
        <div style="display:flex;gap:10px;align-items:flex-start;">
          ${disc}
          <div style="min-width:0;">
            <div style="font-size:14px;font-weight:700;color:#101828;line-height:1.3;">${title}</div>
            ${place ? `<div style="font-size:12px;color:#667085;margin-top:1px;">${place}</div>` : ''}
          </div>
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:10px;">${chips.join('')}</div>
        ${row('When', when && when !== '— → —' ? when : '')}
        ${props.peak_hours ? row('Peak hours', props.peak_hours) : ''}
        ${row('Summary', props.summary)}
        ${pincodes ? row('Pincodes affected', pincodes) : ''}
        ${sources}
      </div>`;
  }, []);

  const clearEvents = useCallback(() => {
    // Cancel any in-flight request
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (e) { /* ignore */ }
      abortRef.current = null;
    }

    // Remove point markers
    markersRef.current.forEach((m) => {
      try { m.remove(); } catch (e) { /* ignore */ }
    });
    markersRef.current = [];
    seenIdsRef.current.clear();

    // Close popup
    if (popupRef.current) {
      try { popupRef.current.remove(); } catch (e) { /* ignore */ }
      popupRef.current = null;
    }

    // Remove line + polygon layers/sources
    const map = mapRef.current;
    if (map) {
      [LINE_LAYER_ID, LINE_CASING_LAYER_ID].forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      if (map.getSource(LINE_SOURCE_ID)) map.removeSource(LINE_SOURCE_ID);

      [POLY_OUTLINE_LAYER_ID, POLY_FILL_LAYER_ID].forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      if (map.getSource(POLY_SOURCE_ID)) map.removeSource(POLY_SOURCE_ID);
    }
    lineFeaturesRef.current = [];
    polygonFeaturesRef.current = [];
    setEventCount(0);
  }, [mapRef]);

  // Open a popup for an event at the given lng/lat.
  const openPopup = useCallback((lngLat, props, uiCategory) => {
    const map = mapRef.current;
    if (!map) return;
    if (popupRef.current) {
      try { popupRef.current.remove(); } catch (e) { /* ignore */ }
    }
    popupRef.current = new maplibregl.Popup({
      offset: 18,
      maxWidth: '340px',
      closeButton: true,
      className: 'events-popup',
      anchor: 'top',
    })
      .setLngLat(lngLat)
      .setHTML(buildPopupHtml(props, uiCategory))
      .addTo(map);

    // Ensure scroll starts at top
    const contentEl = popupRef.current.getElement()?.querySelector('.maplibregl-popup-content');
    if (contentEl) contentEl.scrollTop = 0;
  }, [mapRef, buildPopupHtml]);

  // Render polyline features into a single geojson source/layer.
  const renderLines = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const geojson = { type: 'FeatureCollection', features: lineFeaturesRef.current };

    if (map.getSource(LINE_SOURCE_ID)) {
      map.getSource(LINE_SOURCE_ID).setData(geojson);
      return;
    }

    map.addSource(LINE_SOURCE_ID, { type: 'geojson', data: geojson });

    // White casing underneath for contrast on any basemap.
    map.addLayer({
      id: LINE_CASING_LAYER_ID,
      type: 'line',
      source: LINE_SOURCE_ID,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#FFFFFF', 'line-width': 7, 'line-opacity': 0.9 },
    });
    map.addLayer({
      id: LINE_LAYER_ID,
      type: 'line',
      source: LINE_SOURCE_ID,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'lineColor'],
        'line-width': 4,
        'line-opacity': 0.95,
      },
    });

    // Click a line → popup at the click point.
    map.on('click', LINE_LAYER_ID, (e) => {
      const feature = e.features && e.features[0];
      if (!feature) return;
      const props = feature.properties || {};
      // GeoJSON layer flattens props to strings; JSON-decode the stashed payload.
      let full = props;
      try { full = props._payload ? JSON.parse(props._payload) : props; } catch (err) { /* ignore */ }
      openPopup(e.lngLat, full, props.uiCategory || uiCategoryOf(full.event_type, full.event_subtype));
    });
    map.on('mouseenter', LINE_LAYER_ID, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', LINE_LAYER_ID, () => { map.getCanvas().style.cursor = ''; });
  }, [mapRef, openPopup]);

  // Render polygon features into a single geojson source/layer (fill + outline).
  const renderPolygons = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const geojson = { type: 'FeatureCollection', features: polygonFeaturesRef.current };

    if (map.getSource(POLY_SOURCE_ID)) {
      map.getSource(POLY_SOURCE_ID).setData(geojson);
      return;
    }

    map.addSource(POLY_SOURCE_ID, { type: 'geojson', data: geojson });

    // Translucent fill so the basemap stays legible underneath.
    map.addLayer({
      id: POLY_FILL_LAYER_ID,
      type: 'fill',
      source: POLY_SOURCE_ID,
      paint: {
        'fill-color': ['get', 'lineColor'],
        'fill-opacity': 0.22,
      },
    });
    map.addLayer({
      id: POLY_OUTLINE_LAYER_ID,
      type: 'line',
      source: POLY_SOURCE_ID,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'lineColor'],
        'line-width': 2.5,
        'line-opacity': 0.95,
      },
    });

    // Click inside a polygon → popup at the click point.
    map.on('click', POLY_FILL_LAYER_ID, (e) => {
      const feature = e.features && e.features[0];
      if (!feature) return;
      const props = feature.properties || {};
      let full = props;
      try { full = props._payload ? JSON.parse(props._payload) : props; } catch (err) { /* ignore */ }
      openPopup(e.lngLat, full, props.uiCategory || uiCategoryOf(full.event_type, full.event_subtype));
    });
    map.on('mouseenter', POLY_FILL_LAYER_ID, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', POLY_FILL_LAYER_ID, () => { map.getCanvas().style.cursor = ''; });
  }, [mapRef, openPopup]);

  // Create a DOM marker element for a point event.
  const makeMarkerEl = useCallback((uiCategory) => {
    const { bg, fg } = iconStyle(uiCategory);
    const el = document.createElement('div');
    el.className = 'event-marker';
    el.innerHTML = `
      <div class="event-marker-pin" style="background:${bg};color:${fg};">
        ${glyphSvg(uiCategory, 18, fg)}
      </div>`;
    return el;
  }, []);

  // Fetch events for the current viewport and render them.
  const fetchEvents = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !enabledRef.current) return;

    const bounds = map.getBounds();
    const north = bounds.getNorth();
    const south = bounds.getSouth();
    const east = bounds.getEast();
    const west = bounds.getWest();

    // Abort any previous request before starting a new one.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (e) { /* ignore */ }
    }
    const controller = new AbortController();
    abortRef.current = controller;

    const url = `${EVENTS_API}?north=${north}&south=${south}&east=${east}&west=${west}`;

    let data;
    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) return;
      data = await res.json();
    } catch (e) {
      return; // network error or aborted
    }
    if (!enabledRef.current) return;

    const features = Array.isArray(data?.features) ? data.features : [];

    features.forEach((feature) => {
      const geom = feature?.geometry;
      const props = feature?.properties || {};
      const id = feature?.id || props.event_id;
      if (!geom) return;

      const uiCategory = uiCategoryOf(props.event_type, props.event_subtype);

      if (geom.type === 'Point') {
        if (id && seenIdsRef.current.has(id)) return;
        if (id) seenIdsRef.current.add(id);

        const coords = geom.coordinates;
        if (!Array.isArray(coords) || coords.length < 2) return;

        const el = makeMarkerEl(uiCategory);
        const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat(coords)
          .addTo(map);
        marker._eventId = id;

        el.addEventListener('click', (ev) => {
          ev.stopPropagation();
          openPopup(coords, props, uiCategory);
        });

        markersRef.current.push(marker);
      } else if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
        if (id && seenIdsRef.current.has(id)) return;
        if (id) seenIdsRef.current.add(id);

        lineFeaturesRef.current.push({
          type: 'Feature',
          geometry: geom,
          properties: {
            uiCategory,
            lineColor: iconColor(uiCategory),
            _payload: JSON.stringify(props),
          },
        });
      } else if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
        if (id && seenIdsRef.current.has(id)) return;
        if (id) seenIdsRef.current.add(id);

        polygonFeaturesRef.current.push({
          type: 'Feature',
          geometry: geom,
          properties: {
            uiCategory,
            lineColor: iconColor(uiCategory),
            _payload: JSON.stringify(props),
          },
        });
      }
    });

    if (lineFeaturesRef.current.length) renderLines();
    if (polygonFeaturesRef.current.length) renderPolygons();
    setEventCount(
      markersRef.current.length +
      lineFeaturesRef.current.length +
      polygonFeaturesRef.current.length
    );
  }, [mapRef, makeMarkerEl, openPopup, renderLines, renderPolygons]);

  // Debounced fetch on map movement while enabled.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const handleMoveEnd = () => {
      if (!enabledRef.current) return;
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setTimeout(fetchEvents, FETCH_DEBOUNCE_MS);
    };

    map.on('moveend', handleMoveEnd);
    return () => {
      map.off('moveend', handleMoveEnd);
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    };
  }, [mapRef, fetchEvents]);

  // React to enable/disable.
  useEffect(() => {
    enabledRef.current = isEventsEnabled;
    if (isEventsEnabled) {
      fetchEvents();
    } else {
      clearEvents();
    }
  }, [isEventsEnabled, fetchEvents, clearEvents]);

  const toggleEvents = useCallback(() => {
    setIsEventsEnabled((prev) => !prev);
  }, []);

  return {
    isEventsEnabled,
    eventCount,
    toggleEvents,
    setIsEventsEnabled,
    clearEvents,
  };
};

export default useEvents;
